import React from 'react';

// Kinetic Speedline Radiation (Graphic Inked Vector Action Accent)
export const MangaSpeedlines: React.FC<{ className?: string; opacity?: number }> = ({
  className = '',
  opacity = 0.12,
}) => {
  const rays = Array.from({ length: 48 }, (_, i) => {
    const angle = (i * 360) / 48;
    const width = (i % 2 === 0 ? 1.4 : 0.8) + (i % 3 === 0 ? 1.0 : 0);
    return (
      <line
        key={i}
        x1="500"
        y1="500"
        x2={500 + Math.cos((angle * Math.PI) / 180) * 800}
        y2={500 + Math.sin((angle * Math.PI) / 180) * 800}
        stroke="currentColor"
        strokeWidth={width}
        strokeDasharray={i % 2 === 0 ? "80, 20" : "120, 40"}
      />
    );
  });

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} style={{ opacity }}>
      <svg
        viewBox="0 0 1000 1000"
        className="w-full h-full text-soft-white/20"
        preserveAspectRatio="xMidYMid slice"
      >
        <circle cx="500" cy="500" r="180" fill="transparent" />
        {rays}
      </svg>
    </div>
  );
};
export const KineticSpeedlines = MangaSpeedlines;

// Authentic Screentone Dot Halftone Shading with 3 Densities & Chromatic Variations
export const MangaHalftone: React.FC<{ 
  className?: string;
  density?: 'fine' | 'medium' | 'sparse';
}> = ({ className = '', density = 'medium' }) => {
  const getDensityClass = () => {
    switch (density) {
      case 'fine':
        return 'halftone-fine opacity-30';
      case 'sparse':
        return 'halftone-sparse opacity-25';
      default:
        return 'halftone-medium opacity-20';
    }
  };

  return (
    <div
      className={`absolute inset-0 pointer-events-none ${getDensityClass()} ${className}`}
    />
  );
};
export const HalftoneScreen = MangaHalftone;

// High-Contrast Editorial Technical Impact Badge
export const MangaImpactBadge: React.FC<{
  text: string;
  variant?: 'rose' | 'vermilion' | 'apricot' | 'gold' | 'orchid' | 'teal' | 'jade' | 'burgundy' | 'boom' | 'shatter' | 'oneclick' | 'safe' | 'serious' | 'blue' | 'red' | 'yellow';
  className?: string;
}> = ({ text, variant = 'rose', className = '' }) => {
  const getStyles = () => {
    switch (variant) {
      case 'rose':
      case 'shatter':
      case 'red':
        return 'bg-rose text-white border-rose-light/50 shadow-[2px_2px_0px_0px_rgba(10,8,10,0.95)]';
      case 'vermilion':
      case 'oneclick':
        return 'bg-vermilion text-white border-vermilion-light/50 shadow-[2px_2px_0px_0px_rgba(10,8,10,0.95)]';
      case 'apricot':
      case 'yellow':
      case 'boom':
        return 'bg-apricot text-obsidian border-apricot-light/60 shadow-[2px_2px_0px_0px_rgba(10,8,10,0.95)] font-bold';
      case 'gold':
        return 'bg-gold text-obsidian border-gold-light/60 shadow-[2px_2px_0px_0px_rgba(10,8,10,0.95)] font-bold';
      case 'orchid':
        return 'bg-orchid text-white border-orchid-light/50 shadow-[2px_2px_0px_0px_rgba(10,8,10,0.95)]';
      case 'teal':
        return 'bg-teal text-white border-teal-light/50 shadow-[2px_2px_0px_0px_rgba(10,8,10,0.95)]';
      case 'jade':
      case 'safe':
        return 'bg-jade text-obsidian border-jade-light/60 shadow-[2px_2px_0px_0px_rgba(10,8,10,0.95)] font-bold';
      case 'burgundy':
      case 'serious':
      case 'blue':
      default:
        return 'bg-burgundy text-ivory border-rose/30 shadow-[2px_2px_0px_0px_rgba(10,8,10,0.95)]';
    }
  };

  return (
    <span
      className={`inline-block px-3 py-0.5 text-xs font-black uppercase tracking-wider font-mono border rounded-sm transform active:scale-95 transition-transform ${getStyles()} ${className}`}
      style={{
        letterSpacing: '0.06em',
      }}
    >
      {text}
    </span>
  );
};
export const TacticalBadge = MangaImpactBadge;

// Graphic Section Marker / Technical System Ribbon
export const MangaChapterMarker: React.FC<{
  volume?: string;
  chapter?: string;
  title: string;
  className?: string;
}> = ({ volume = 'SPEC', chapter = '01', title, className = '' }) => {
  const cleanVolume = volume.replace(/VOL\.\s*/i, 'SPEC ').trim();
  const cleanChapter = chapter.replace(/ACT\s*/i, '// ').trim();

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-charcoal/90 border border-white/[0.10] shadow-[2px_2px_0px_0px_rgba(10,8,10,0.9)] text-xs font-mono font-bold tracking-wider uppercase backdrop-blur-md ${className}`}>
      <span className="px-2 py-0.5 rounded bg-gradient-action text-white text-[10px] font-black tracking-widest shadow-sm">
        {cleanVolume}
      </span>
      <span className="text-apricot font-bold">{cleanChapter}</span>
      <span className="text-muted/40">|</span>
      <span className="text-soft-white tracking-tight">{title}</span>
    </div>
  );
};
export const GraphicSectionMarker = MangaChapterMarker;

// Inked Technical Briefing Box
export const MangaNarratorBox: React.FC<{
  speaker?: string;
  children: React.ReactNode;
  variant?: 'dark' | 'alert' | 'highlight';
  className?: string;
}> = ({ speaker = 'OPERATOR BRIEF', children, variant = 'dark', className = '' }) => {
  const getVariant = () => {
    switch (variant) {
      case 'alert':
        return 'bg-plum-black/95 border-l-4 border-rose text-soft-white';
      case 'highlight':
        return 'bg-plum-black/95 border-l-4 border-jade text-soft-white';
      default:
        return 'bg-charcoal/95 border-l-4 border-vermilion text-soft-white';
    }
  };

  return (
    <div className={`p-4 rounded-sm border border-white/[0.08] shadow-[3px_3px_0px_0px_rgba(10,8,10,0.95)] text-xs font-mono leading-relaxed relative ${getVariant()} ${className}`}>
      <div className="text-[10px] font-black tracking-widest text-muted uppercase mb-1.5 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 bg-rose rounded-full animate-pulse" />
        <span className="text-ivory font-bold">{speaker}</span>
      </div>
      <div className="text-soft-white/90 font-medium">{children}</div>
    </div>
  );
};
export const ExecutiveBriefBox = MangaNarratorBox;
